/**
 * SettingsSyncSection
 *
 * Settings section for managing external calendar sync:
 * linked accounts, incoming (pull) calendars, and outgoing (push) calendars.
 *
 * External sync is not finished, so it ships to production behind an
 * admin-only preview gate (`useExternalSyncEnabled`): admins get the real
 * panel, everyone else gets a "coming soon" placeholder. See that hook for
 * the rationale and for how to remove the gate when sync ships.
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  RefreshCw,
  Trash2,
  Loader2,
  Unplug,
  Plus,
  RefreshCcw,
  Lock,
  AlertTriangle,
} from 'lucide-react';
import { Section } from './settings-helpers';
import { SyncStatusBadge } from './SyncStatusBadge';
import { ConnectProviderDialog } from './ConnectProviderDialog';
import { RemoteCalendarPicker } from './RemoteCalendarPicker';
import {
  useSyncConnections,
  useDisconnectSync,
  SYNC_OAUTH_CHANNEL,
} from '../hooks/useSyncConnections';
import {
  useSyncJobs,
  useTriggerSync,
  useDeleteSyncJob,
  useUpdateSyncJobSettings,
} from '../hooks/useSyncJobs';
import { useExternalSyncEnabled } from '../hooks/useExternalSyncEnabled';
import { api } from '@/lib/api';
import type { SyncConnection, SyncJob, CalendarCollectionDetail } from '@/lib/api';

const providerLabels: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
};

interface SettingsSyncSectionProps {
  calendars?: CalendarCollectionDetail[];
  defaultOpen?: boolean;
}

export function SettingsSyncSection({ calendars = [], defaultOpen }: SettingsSyncSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const syncEnabled = useExternalSyncEnabled();

  useEffect(() => {
    if (defaultOpen && sectionRef.current) {
      // Small delay to let the sheet animation finish
      const timer = setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [defaultOpen]);

  return (
    <div ref={sectionRef}>
      {syncEnabled ? <ExternalSyncPanel calendars={calendars} /> : <ExternalSyncComingSoon />}
    </div>
  );
}

/**
 * Stand-in shown to everyone outside the external-sync preview audience.
 *
 * The section stays in place rather than disappearing: the settings sheet
 * keeps one shape for every account, and "coming soon" is the honest answer —
 * this is unfinished, not unavailable to you specifically. The Connect
 * Account button is rendered disabled rather than omitted so the shape of the
 * finished feature is legible at a glance.
 */
function ExternalSyncComingSoon() {
  return (
    <Section icon={RefreshCcw} title="External Sync">
      <div className="flex items-start gap-2.5 rounded-lg border border-dashed bg-muted/30 px-3 py-3">
        <Lock className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
        <div className="space-y-1">
          <p className="text-xs font-medium text-foreground">Coming soon</p>
          <p className="text-[11px] leading-snug text-muted-foreground">
            Two-way sync with Google, Microsoft, and Apple calendars is on its way. We&apos;re
            putting the finishing touches on it.
          </p>
        </div>
      </div>

      <Button variant="outline" size="sm" className="w-full gap-2" disabled>
        <Plus className="h-3.5 w-3.5" />
        Connect Account
      </Button>
    </Section>
  );
}

/**
 * The real External Sync UI.
 *
 * Split out of `SettingsSyncSection` so it mounts ONLY when the gate is open.
 * A conditional render is what keeps the promise — React hooks cannot be
 * called conditionally, so leaving the sync queries in the outer component
 * would have every non-admin fire `/sync/connections` + `/sync/jobs` on each
 * settings open and collect a pair of 403s for a feature they cannot see.
 */
function ExternalSyncPanel({ calendars = [] }: { calendars?: CalendarCollectionDetail[] }) {
  const confirm = useConfirm();
  const { data: connections = [], isLoading: loadingConns } = useSyncConnections();
  const { data: jobs = [], isLoading: loadingJobs } = useSyncJobs();
  const disconnectSync = useDisconnectSync();
  const triggerSync = useTriggerSync();
  const deleteJob = useDeleteSyncJob();
  const updateJobSettings = useUpdateSyncJobSettings();

  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [pickerConnectionId, setPickerConnectionId] = useState<string | null>(null);
  // Set when an OAuth callback reports success; the picker then opens for the
  // connection that has no jobs yet, so connecting flows straight into choosing
  // what to sync instead of dead-ending on a success message.
  const [autoOpenPending, setAutoOpenPending] = useState(false);

  const isLoading = loadingConns || loadingJobs;

  // Connecting an account is never the goal in itself — the user connects in
  // order to sync something. Listen for the OAuth callback's completion and
  // open the picker for the connection that has no jobs yet, so the flow
  // continues instead of dead-ending on a success message that disappears.
  // The Apple path reaches the same state via `onConnected` on the dialog
  // below (it has no callback page to broadcast from).
  useEffect(() => {
    const channel = new BroadcastChannel(SYNC_OAUTH_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data?.type === 'oauth-complete') setAutoOpenPending(true);
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    if (!autoOpenPending || loadingConns || loadingJobs) return;
    const unconfigured = connections.find((c) => !jobs.some((j) => j.connectionId === c.id));
    if (unconfigured) {
      setPickerConnectionId(unconfigured.id);
      setAutoOpenPending(false);
    }
  }, [autoOpenPending, connections, jobs, loadingConns, loadingJobs]);

  /** Shown between the provider calendar and its paired Fias calendar. */
  const DIRECTION_GLYPH: Record<string, string> = {
    pull_only: '\u2192', // into Fias
    push_only: '\u2190', // out of Fias
    bidirectional: '\u21c4',
  };

  const jobsForConnection = (connectionId: string): SyncJob[] =>
    jobs.filter((j) => j.connectionId === connectionId);

  const localCalendarName = (localResourceId: string | null | undefined): string => {
    if (!localResourceId) return 'Default Calendar';
    const cal = calendars.find((c) => c.id === localResourceId);
    return cal?.name ?? 'Calendar';
  };

  const handleDisconnect = async (conn: SyncConnection) => {
    // §10 disconnect contract: explicit keep-vs-remove with per-category
    // counts — never silently delete. "Remove" targets only events imported
    // from the provider; events born in Fias (or adopted) always stay.
    let removable = 0;
    let keptLocal = 0;
    try {
      const preview = await api.calendarSyncDisconnectPreview(conn.id);
      removable = preview.removable;
      keptLocal = preview.keptLocal;
    } catch {
      // Preview is best-effort — fall through to the keep-only prompt.
    }
    const label = providerLabels[conn.provider] ?? conn.provider;
    if (
      !(await confirm({
        title: `Disconnect ${label}?`,
        description:
          'Syncing stops and the sync configuration is removed.' +
          (keptLocal > 0 ? ` ${keptLocal} of your own event(s) always stay.` : '') +
          // Microsoft's identity platform has no programmatic consent
          // revocation for personal accounts — honest guidance beats a
          // silently-still-live grant (docs/calendar-sync.md).
          (conn.provider === 'microsoft'
            ? '\n\nNote: Microsoft does not let apps revoke their own access. To fully revoke it, remove Fias at account.live.com/consent.'
            : ''),
        confirmLabel: 'Disconnect',
        destructive: true,
      }))
    ) {
      return;
    }
    const remove =
      removable > 0 &&
      (await confirm({
        title: `Also delete the ${removable} event(s) that came from ${label}?`,
        description: 'Keeping them leaves every event in Fias.',
        confirmLabel: 'Delete them',
        cancelLabel: 'Keep them',
        destructive: true,
      }));
    await disconnectSync.mutateAsync({ connectionId: conn.id, mode: remove ? 'remove' : 'keep' });
  };

  const handleDeleteJob = async (job: SyncJob) => {
    // Mirrors the connection-level disconnect contract (§10): explicit
    // keep-vs-remove with a count, never a silent delete. Without this,
    // removing a synced calendar left every event it had imported behind with
    // no route to delete them — re-syncing a subscription (a holidays feed,
    // say) then piled up duplicates permanently.
    const label = job.remoteResourceName ?? 'this calendar';
    let removable = 0;
    let keptLocal = 0;
    try {
      const preview = await api.calendarSyncJobDeletePreview(job.id);
      removable = preview.removable;
      keptLocal = preview.keptLocal;
    } catch {
      // Preview is best-effort — fall through to the keep-only prompt.
    }

    if (
      !(await confirm({
        title: `Stop syncing "${label}"?`,
        // Only events Fias can prove are the user's own. This used to count
        // ADOPTED events too and describe them as "created in Fias", which
        // was simply false for imported events and made it look as though
        // your own data had been deleted.
        description: keptLocal > 0 ? `${keptLocal} of your own event(s) always stay.` : undefined,
        confirmLabel: 'Stop syncing',
        destructive: true,
      }))
    ) {
      return;
    }

    const remove =
      removable > 0 &&
      (await confirm({
        title: `Also delete the ${removable} event(s) that came from "${label}"?`,
        description: 'Keeping them leaves every event in Fias.',
        confirmLabel: 'Delete them',
        cancelLabel: 'Keep them',
        destructive: true,
      }));

    await deleteJob.mutateAsync({ jobId: job.id, mode: remove ? 'remove' : 'keep' });
  };

  const handleDirectionChange = (jobId: string, direction: string) => {
    updateJobSettings.mutate({
      jobId,
      settings: { direction },
    });
  };

  const handleAutoSyncToggle = (jobId: string, enabled: boolean) => {
    updateJobSettings.mutate({
      jobId,
      settings: { autoSyncEnabled: enabled },
    });
  };

  const handleConflictStrategyChange = (jobId: string, strategy: string) => {
    updateJobSettings.mutate({
      jobId,
      settings: { conflictStrategy: strategy },
    });
  };

  return (
    <>
      <Section icon={RefreshCcw} title="External Sync">
        {/* Connect Account */}
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={() => setConnectDialogOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Connect Account
        </Button>

        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && connections.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">
            No external accounts connected. Connect one to start syncing calendars.
          </p>
        )}

        {/* Per-connection cards */}
        {!isLoading &&
          connections.map((conn) => {
            const connJobs = jobsForConnection(conn.id);

            return (
              <div key={conn.id} className="rounded-lg border bg-card">
                {/* Connection header */}
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {providerLabels[conn.provider] ?? conn.provider}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {conn.providerAccountEmail ?? conn.providerAccountName ?? 'Connected'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <SyncStatusBadge status={conn.status} />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDisconnect(conn)}
                      disabled={disconnectSync.isPending}
                      title="Disconnect account"
                    >
                      <Unplug className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="p-3 space-y-2">
                  {/* ONE list. A sync job pairs a Fias calendar with a
                      provider calendar and carries a direction — splitting the
                      UI into "Importing" / "Exporting" rendered every
                      `bidirectional` job TWICE, once under each heading, and
                      implied two kinds of thing where there is one. */}
                  {connJobs.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      No calendars are syncing with this account yet.
                    </p>
                  ) : (
                    connJobs.map((job) => (
                      <SyncJobCard
                        key={job.id}
                        job={job}
                        label={job.remoteResourceName ?? 'Calendar'}
                        sublabel={`${DIRECTION_GLYPH[job.direction] ?? '\u2192'} ${localCalendarName(job.localResourceId)}`}
                        onTriggerSync={() => triggerSync.mutate(job.id)}
                        onDelete={() => handleDeleteJob(job)}
                        onDirectionChange={(dir) => handleDirectionChange(job.id, dir)}
                        onAutoSyncToggle={(enabled) => handleAutoSyncToggle(job.id, enabled)}
                        onConflictStrategyChange={(st) => handleConflictStrategyChange(job.id, st)}
                        isSyncing={triggerSync.isPending}
                        isDeleting={deleteJob.isPending}
                      />
                    ))
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full gap-1.5 text-xs h-7"
                    onClick={() => setPickerConnectionId(conn.id)}
                  >
                    <Plus className="h-3 w-3" />
                    Sync Calendar
                  </Button>
                </div>
              </div>
            );
          })}
      </Section>

      {/* Dialogs */}
      {/* Apple is not OAuth — no popup, no callback page, so it never emits the
          `oauth-complete` broadcast the effect above listens for. Without this
          callback an Apple connect closes the dialog and dead-ends on a card
          reading "No calendars are syncing with this account yet", while
          Google/Microsoft flow straight into the picker. Same destination,
          reached the same way: mark a connect as pending and let the effect
          open the picker once the connection list has refetched. */}
      <ConnectProviderDialog
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
        onConnected={() => setAutoOpenPending(true)}
      />

      {pickerConnectionId && (
        <RemoteCalendarPicker
          connectionId={pickerConnectionId}
          open={!!pickerConnectionId}
          onOpenChange={(o) => {
            if (!o) setPickerConnectionId(null);
          }}
        />
      )}
    </>
  );
}

/**
 * Provider access roles that forbid writing. Mirrors `isReadOnlyAccessRole` in
 * @fias/sync-engine — duplicated rather than imported because the client must
 * not pull in a server-side package. Unknown (null) means writable, so
 * pre-existing jobs and providers that report no role behave as before.
 */
function isReadOnlyRole(role: string | null | undefined): boolean {
  return role === 'reader' || role === 'freeBusyReader';
}

/* ── Sync job card sub-component ── */

interface SyncJobCardProps {
  job: SyncJob;
  label: string;
  sublabel: string;
  onTriggerSync: () => void;
  onDelete: () => void;
  onDirectionChange: (direction: string) => void;
  onAutoSyncToggle: (enabled: boolean) => void;
  onConflictStrategyChange: (strategy: string) => void;
  isSyncing: boolean;
  isDeleting: boolean;
}

function SyncJobCard({
  job,
  label,
  sublabel,
  onTriggerSync,
  onDelete,
  onDirectionChange,
  onAutoSyncToggle,
  onConflictStrategyChange,
  isSyncing,
  isDeleting,
}: SyncJobCardProps) {
  const readOnlyRemote = isReadOnlyRole(job.remoteAccessRole);

  return (
    <div className="rounded-md border px-3 py-2 space-y-2">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium truncate">{label}</p>
          <p className="text-[11px] text-muted-foreground truncate">{sublabel}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <SyncStatusBadge status={job.lastSyncStatus} lastSyncedAt={job.lastSyncedAt} />
            {/* A run does NOT fail because individual events did — one
                unparseable event among fifty shouldn't mark the sync failed,
                so they go to the retry inbox and the run still reports
                success. The cost is that a totally-failing pull is
                indistinguishable from a clean one: a CalDAV parsing bug once
                sent 100% of incoming events to that inbox while this badge
                read "Synced" and push kept working. Showing the count is what
                makes that visible.

                NOTE: this is a PER-RUN figure. `last_sync_stats` is
                overwritten on every run (sync-engine/jobs.ts), so a run that
                pulls nothing new reports 0 and the badge vanishes while those
                rows still sit in sync_event_failures. The tooltip says so.
                Showing the true backlog needs an open-failure count on the
                job payload — worth doing, not done here. */}
            {(job.lastSyncStats?.failuresRecorded ?? 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-500"
                title="Items the last sync run could not read; they are queued for automatic retry. This counts that run only — a later run reporting none does not mean the earlier failures were resolved."
              >
                <AlertTriangle className="h-3 w-3" />
                {job.lastSyncStats.failuresRecorded}{' '}
                {job.lastSyncStats.failuresRecorded === 1 ? 'item' : 'items'} couldn&apos;t sync
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onTriggerSync}
            disabled={isSyncing}
            title="Sync now"
          >
            <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            disabled={isDeleting}
            title="Remove sync"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Expandable settings */}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
          Settings
        </summary>
        <div className="mt-2 space-y-2.5 pl-1">
          {/* Direction. A read-only provider calendar (holidays, sports, a
              shared calendar you don't own) rejects every write, so there is
              no direction to choose — offering the control would only let the
              user pick a combination the provider refuses, which then fails
              invisibly in the retry inbox. Show the fixed value instead. */}
          {readOnlyRemote ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Direction</span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Lock className="h-3 w-3" />
                Import only (read-only)
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Direction</span>
              <Select value={job.direction} onValueChange={onDirectionChange}>
                <SelectTrigger className="h-6 w-[120px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pull_only">Import only</SelectItem>
                  <SelectItem value="push_only">Export only</SelectItem>
                  <SelectItem value="bidirectional">Both ways</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Auto-sync */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Auto-sync</span>
            <Switch
              checked={job.autoSyncEnabled}
              onCheckedChange={onAutoSyncToggle}
              className="scale-75 origin-right"
            />
          </div>

          {/* Conflict strategy — only meaningful when BOTH sides can change.
              A one-way job has no conflicts to resolve, and a read-only remote
              can never be one of the two writers. */}
          {job.direction === 'bidirectional' && !readOnlyRemote && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Conflicts</span>
              <Select value={job.conflictStrategy} onValueChange={onConflictStrategyChange}>
                <SelectTrigger className="h-6 w-[130px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_write_wins">Latest wins</SelectItem>
                  <SelectItem value="local_wins">Local wins</SelectItem>
                  <SelectItem value="remote_wins">Remote wins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
